/**
 * EU AI Act Compliance Requirements Database
 * Based on Regulation (EU) 2024/1689
 */

import { ComplianceRequirement, ChecklistItem } from './eu-ai-act-types';

/**
 * Requirements for High-Risk AI Systems (Articles 9-15, 43)
 */
export const HIGH_RISK_REQUIREMENTS: ComplianceRequirement[] = [
  {
    id: 'risk-management',
    article: 'Article 9',
    title: 'Risk Management System',
    description: 'Establish, implement, document and maintain a risk management system throughout the entire lifecycle of the high-risk AI system.',
    category: 'risk-management',
    priority: 'critical',
    applicableRiskLevels: ['high'],
    checklistItems: [
      {
        id: 'rm-1',
        text: 'Identify and analyze known and reasonably foreseeable risks',
        description: 'Document all potential risks associated with the AI system when used in accordance with its intended purpose',
        status: 'not-started',
      },
      {
        id: 'rm-2',
        text: 'Estimate and evaluate risks from reasonably foreseeable misuse',
        description: 'Consider how the system might be misused and document associated risks',
        status: 'not-started',
      },
      {
        id: 'rm-3',
        text: 'Evaluate risks from data analysis and system interaction',
        description: 'Assess risks that may emerge from data used for training and system outputs',
        status: 'not-started',
      },
      {
        id: 'rm-4',
        text: 'Adopt risk mitigation measures',
        description: 'Implement appropriate design, testing, and validation measures to eliminate or reduce risks',
        status: 'not-started',
      },
      {
        id: 'rm-5',
        text: 'Ensure residual risks are acceptable',
        description: 'Document that any remaining risks are acceptable when weighed against benefits',
        status: 'not-started',
      },
      {
        id: 'rm-6',
        text: 'Test risk management measures',
        description: 'Conduct testing to verify risk mitigation measures are effective',
        status: 'not-started',
      },
      {
        id: 'rm-7',
        text: 'Establish risk management process documentation',
        description: 'Maintain complete documentation of the risk management process',
        status: 'not-started',
      },
    ],
  },
  {
    id: 'data-governance',
    article: 'Article 10',
    title: 'Data and Data Governance',
    description: 'Implement appropriate data governance and management practices for training, validation, and testing datasets.',
    category: 'data-governance',
    priority: 'critical',
    applicableRiskLevels: ['high'],
    checklistItems: [
      {
        id: 'dg-1',
        text: 'Define data governance policies',
        description: 'Establish policies covering data collection, processing, and use for AI training',
        status: 'not-started',
      },
      {
        id: 'dg-2',
        text: 'Ensure training data is relevant and representative',
        description: 'Verify data is appropriate for the intended purpose and reflects real-world distribution',
        status: 'not-started',
      },
      {
        id: 'dg-3',
        text: 'Examine data for errors and gaps',
        description: 'Implement processes to identify and address data quality issues',
        status: 'not-started',
      },
      {
        id: 'dg-4',
        text: 'Implement bias detection and mitigation',
        description: 'Identify and address potential biases in training data',
        status: 'not-started',
      },
      {
        id: 'dg-5',
        text: 'Document data provenance',
        description: 'Maintain records of data sources, processing steps, and transformations',
        status: 'not-started',
      },
      {
        id: 'dg-6',
        text: 'Establish data retention policies',
        description: 'Define how long data is retained and when it should be deleted',
        status: 'not-started',
      },
    ],
  },
  {
    id: 'technical-documentation',
    article: 'Article 11',
    title: 'Technical Documentation',
    description: 'Draw up technical documentation demonstrating compliance with AI Act requirements before placing on market.',
    category: 'technical-documentation',
    priority: 'high',
    applicableRiskLevels: ['high'],
    checklistItems: [
      {
        id: 'td-1',
        text: 'Document general system description',
        description: 'Describe intended purpose, provider info, version, and how system interacts with other systems',
        status: 'not-started',
      },
      {
        id: 'td-2',
        text: 'Detail design specifications',
        description: 'Document system architecture, algorithms, data requirements, and development process',
        status: 'not-started',
      },
      {
        id: 'td-3',
        text: 'Describe monitoring and functioning',
        description: 'Document how the system operates and how it is monitored in production',
        status: 'not-started',
      },
      {
        id: 'td-4',
        text: 'Include risk management documentation',
        description: 'Reference the risk management system and measures taken',
        status: 'not-started',
      },
      {
        id: 'td-5',
        text: 'Document validation and testing',
        description: 'Include test procedures, metrics used, and results achieved',
        status: 'not-started',
      },
      {
        id: 'td-6',
        text: 'Describe modifications and updates',
        description: 'Document any changes made after initial market placement',
        status: 'not-started',
      },
    ],
  },
  {
    id: 'record-keeping',
    article: 'Article 12',
    title: 'Record-Keeping',
    description: 'Ensure automatic recording of events (logs) throughout the system lifecycle for traceability.',
    category: 'record-keeping',
    priority: 'high',
    applicableRiskLevels: ['high'],
    checklistItems: [
      {
        id: 'rk-1',
        text: 'Implement automatic logging',
        description: 'Enable logging of system operations, inputs, outputs, and events',
        status: 'not-started',
      },
      {
        id: 'rk-2',
        text: 'Log period of use and user identity',
        description: 'Record when the system is used and by whom (where applicable)',
        status: 'not-started',
      },
      {
        id: 'rk-3',
        text: 'Enable traceability of AI outputs',
        description: 'Ensure outputs can be traced back to inputs and processing steps',
        status: 'not-started',
      },
      {
        id: 'rk-4',
        text: 'Secure log storage',
        description: 'Protect logs from unauthorized access, modification, or deletion',
        status: 'not-started',
      },
      {
        id: 'rk-5',
        text: 'Define log retention periods',
        description: 'Establish appropriate retention periods aligned with regulatory requirements',
        status: 'not-started',
      },
    ],
  },
  {
    id: 'transparency',
    article: 'Article 13',
    title: 'Transparency and Provision of Information',
    description: 'Design systems to enable deployers to interpret outputs and use the system appropriately.',
    category: 'transparency',
    priority: 'high',
    applicableRiskLevels: ['high'],
    checklistItems: [
      {
        id: 'tr-1',
        text: 'Create clear instructions for use',
        description: 'Provide documentation enabling deployers to understand and properly use the system',
        status: 'not-started',
      },
      {
        id: 'tr-2',
        text: 'Describe intended purpose clearly',
        description: 'Clearly state what the system is designed to do and any limitations',
        status: 'not-started',
      },
      {
        id: 'tr-3',
        text: 'Specify expected level of accuracy',
        description: 'Document the system performance metrics and expected accuracy levels',
        status: 'not-started',
      },
      {
        id: 'tr-4',
        text: 'Explain output interpretation',
        description: 'Provide guidance on how to interpret system outputs',
        status: 'not-started',
      },
      {
        id: 'tr-5',
        text: 'Document known limitations',
        description: 'Describe circumstances where the system may not perform as expected',
        status: 'not-started',
      },
      {
        id: 'tr-6',
        text: 'Provide human oversight requirements',
        description: 'Specify the level of human oversight needed for safe operation',
        status: 'not-started',
      },
    ],
  },
  {
    id: 'human-oversight',
    article: 'Article 14',
    title: 'Human Oversight',
    description: 'Design systems to be effectively overseen by natural persons during use.',
    category: 'human-oversight',
    priority: 'critical',
    applicableRiskLevels: ['high'],
    checklistItems: [
      {
        id: 'ho-1',
        text: 'Design for human oversight capability',
        description: 'Build the system to allow meaningful human intervention',
        status: 'not-started',
      },
      {
        id: 'ho-2',
        text: 'Provide override or intervention mechanisms',
        description: 'Enable humans to override, correct, or stop system outputs',
        status: 'not-started',
      },
      {
        id: 'ho-3',
        text: 'Enable understanding of system capabilities',
        description: 'Ensure overseers can understand what the system can and cannot do',
        status: 'not-started',
      },
      {
        id: 'ho-4',
        text: 'Allow monitoring during operation',
        description: 'Provide tools for real-time monitoring of system behavior',
        status: 'not-started',
      },
      {
        id: 'ho-5',
        text: 'Document oversight procedures',
        description: 'Create procedures for human oversight activities',
        status: 'not-started',
      },
      {
        id: 'ho-6',
        text: 'Train oversight personnel',
        description: 'Ensure people responsible for oversight are properly trained',
        status: 'not-started',
      },
    ],
  },
  {
    id: 'accuracy-robustness',
    article: 'Article 15',
    title: 'Accuracy, Robustness and Cybersecurity',
    description: 'Achieve appropriate levels of accuracy, robustness, and cybersecurity throughout the lifecycle.',
    category: 'accuracy-robustness',
    priority: 'high',
    applicableRiskLevels: ['high'],
    checklistItems: [
      {
        id: 'ar-1',
        text: 'Define and achieve accuracy metrics',
        description: 'Establish accuracy requirements and verify they are met',
        status: 'not-started',
      },
      {
        id: 'ar-2',
        text: 'Implement robustness measures',
        description: 'Ensure system handles edge cases and adversarial inputs appropriately',
        status: 'not-started',
      },
      {
        id: 'ar-3',
        text: 'Conduct cybersecurity assessment',
        description: 'Evaluate and address cybersecurity risks and vulnerabilities',
        status: 'not-started',
      },
      {
        id: 'ar-4',
        text: 'Protect against unauthorized access',
        description: 'Implement access controls and authentication mechanisms',
        status: 'not-started',
      },
      {
        id: 'ar-5',
        text: 'Test for resilience',
        description: 'Verify system performs reliably under various conditions',
        status: 'not-started',
      },
      {
        id: 'ar-6',
        text: 'Address data poisoning risks',
        description: 'Protect against malicious manipulation of training or input data',
        status: 'not-started',
      },
    ],
  },
  {
    id: 'conformity-assessment',
    article: 'Article 43',
    title: 'Conformity Assessment',
    description: 'Conduct conformity assessment before placing high-risk AI system on market.',
    category: 'conformity-assessment',
    priority: 'critical',
    applicableRiskLevels: ['high'],
    checklistItems: [
      {
        id: 'ca-1',
        text: 'Determine assessment procedure',
        description: 'Identify whether self-assessment or third-party assessment is required',
        status: 'not-started',
      },
      {
        id: 'ca-2',
        text: 'Prepare technical documentation',
        description: 'Compile all required technical documentation for assessment',
        status: 'not-started',
      },
      {
        id: 'ca-3',
        text: 'Conduct or commission assessment',
        description: 'Perform the conformity assessment procedure',
        status: 'not-started',
      },
      {
        id: 'ca-4',
        text: 'Draw up EU declaration of conformity',
        description: 'Prepare the declaration confirming compliance with requirements',
        status: 'not-started',
      },
      {
        id: 'ca-5',
        text: 'Affix CE marking',
        description: 'Apply the CE marking to compliant systems',
        status: 'not-started',
      },
      {
        id: 'ca-6',
        text: 'Retain assessment documentation',
        description: 'Keep assessment records for 10 years after market placement',
        status: 'not-started',
      },
    ],
  },
  {
    id: 'registration',
    article: 'Article 71',
    title: 'EU Database Registration',
    description: 'Register high-risk AI systems in the EU database before placing on market.',
    category: 'registration',
    priority: 'high',
    applicableRiskLevels: ['high'],
    checklistItems: [
      {
        id: 'reg-1',
        text: 'Prepare registration information',
        description: 'Compile all required information for database registration',
        status: 'not-started',
      },
      {
        id: 'reg-2',
        text: 'Register in EU database',
        description: 'Submit registration to the EU AI database',
        status: 'not-started',
      },
      {
        id: 'reg-3',
        text: 'Keep registration updated',
        description: 'Update registration when system changes substantially',
        status: 'not-started',
      },
    ],
  },
  {
    id: 'post-market-monitoring',
    article: 'Article 72',
    title: 'Post-Market Monitoring',
    description: 'Establish and document a post-market monitoring system proportionate to the nature of the AI system.',
    category: 'post-market-monitoring',
    priority: 'high',
    applicableRiskLevels: ['high'],
    checklistItems: [
      {
        id: 'pmm-1',
        text: 'Design post-market monitoring system',
        description: 'Create a system for collecting and analyzing post-market data',
        status: 'not-started',
      },
      {
        id: 'pmm-2',
        text: 'Collect performance data',
        description: 'Gather data on system performance in real-world use',
        status: 'not-started',
      },
      {
        id: 'pmm-3',
        text: 'Monitor for incidents',
        description: 'Track and investigate any incidents or malfunctions',
        status: 'not-started',
      },
      {
        id: 'pmm-4',
        text: 'Report serious incidents',
        description: 'Report serious incidents to authorities within required timeframes',
        status: 'not-started',
      },
      {
        id: 'pmm-5',
        text: 'Take corrective actions',
        description: 'Implement corrections when issues are identified',
        status: 'not-started',
      },
    ],
  },
];

/**
 * Requirements for Limited Risk AI Systems (Article 50)
 */
export const LIMITED_RISK_REQUIREMENTS: ComplianceRequirement[] = [
  {
    id: 'transparency-limited',
    article: 'Article 50',
    title: 'Transparency Obligations',
    description: 'Ensure users are informed when interacting with AI systems.',
    category: 'transparency',
    priority: 'medium',
    applicableRiskLevels: ['limited'],
    checklistItems: [
      {
        id: 'tl-1',
        text: 'Disclose AI interaction to users',
        description: 'Inform users they are interacting with an AI system (unless obvious from context)',
        status: 'not-started',
      },
      {
        id: 'tl-2',
        text: 'Mark AI-generated content',
        description: 'Label content as AI-generated in a machine-readable format',
        status: 'not-started',
      },
      {
        id: 'tl-3',
        text: 'Disclose emotion recognition use',
        description: 'Inform persons when emotion recognition is being used',
        status: 'not-started',
      },
      {
        id: 'tl-4',
        text: 'Label deep fakes',
        description: 'Disclose when content has been artificially generated or manipulated',
        status: 'not-started',
      },
    ],
  },
];

/**
 * Requirements for General Purpose AI (GPAI) Systems
 */
export const GPAI_REQUIREMENTS: ComplianceRequirement[] = [
  {
    id: 'gpai-documentation',
    article: 'Article 53',
    title: 'GPAI Technical Documentation',
    description: 'Draw up and maintain technical documentation for general-purpose AI models.',
    category: 'technical-documentation',
    priority: 'high',
    applicableRiskLevels: ['high', 'limited'],
    checklistItems: [
      {
        id: 'gpai-1',
        text: 'Document model training details',
        description: 'Describe training methodologies, data sources, and computational resources',
        status: 'not-started',
      },
      {
        id: 'gpai-2',
        text: 'Provide model capabilities information',
        description: 'Document what the model can and cannot do',
        status: 'not-started',
      },
      {
        id: 'gpai-3',
        text: 'Create downstream provider documentation',
        description: 'Provide information enabling compliance by downstream providers',
        status: 'not-started',
      },
      {
        id: 'gpai-4',
        text: 'Document copyright compliance',
        description: 'Demonstrate compliance with copyright and related rights',
        status: 'not-started',
      },
    ],
  },
  {
    id: 'gpai-systemic-risk',
    article: 'Article 55',
    title: 'Systemic Risk GPAI Obligations',
    description: 'Additional obligations for GPAI models with systemic risk.',
    category: 'risk-management',
    priority: 'critical',
    applicableRiskLevels: ['high'],
    checklistItems: [
      {
        id: 'gpai-sr-1',
        text: 'Conduct model evaluation',
        description: 'Perform standardized evaluation including adversarial testing',
        status: 'not-started',
      },
      {
        id: 'gpai-sr-2',
        text: 'Assess and mitigate systemic risks',
        description: 'Identify and address potential systemic risks',
        status: 'not-started',
      },
      {
        id: 'gpai-sr-3',
        text: 'Track and report serious incidents',
        description: 'Monitor for and report serious incidents to authorities',
        status: 'not-started',
      },
      {
        id: 'gpai-sr-4',
        text: 'Ensure cybersecurity protection',
        description: 'Implement adequate cybersecurity measures for the model',
        status: 'not-started',
      },
    ],
  },
];

/**
 * All requirements combined
 */
export const ALL_REQUIREMENTS: ComplianceRequirement[] = [
  ...HIGH_RISK_REQUIREMENTS,
  ...LIMITED_RISK_REQUIREMENTS,
  ...GPAI_REQUIREMENTS,
];

/**
 * Get requirements by risk level
 */
export function getRequirementsByRiskLevel(riskLevel: string): ComplianceRequirement[] {
  return ALL_REQUIREMENTS.filter((r) => r.applicableRiskLevels.includes(riskLevel as any));
}

/**
 * Get requirements by category
 */
export function getRequirementsByCategory(category: string): ComplianceRequirement[] {
  return ALL_REQUIREMENTS.filter((r) => r.category === category);
}

/**
 * Industry sectors and their typical AI use cases
 */
export const INDUSTRY_AI_USE_CASES = {
  healthcare: [
    'Medical diagnosis assistance',
    'Drug discovery',
    'Patient risk stratification',
    'Medical imaging analysis',
    'Treatment recommendation',
  ],
  finance: [
    'Credit scoring',
    'Fraud detection',
    'Algorithmic trading',
    'Customer service chatbots',
    'Risk assessment',
  ],
  education: [
    'Automated grading',
    'Student admission assessment',
    'Learning path personalization',
    'Plagiarism detection',
    'Student performance prediction',
  ],
  employment: [
    'Resume screening',
    'Candidate assessment',
    'Performance evaluation',
    'Workforce planning',
    'Interview scheduling',
  ],
  'law-enforcement': [
    'Predictive policing',
    'Evidence analysis',
    'Facial recognition',
    'Risk assessment',
    'Surveillance systems',
  ],
  'critical-infrastructure': [
    'Grid management',
    'Traffic control',
    'Water treatment optimization',
    'Predictive maintenance',
    'Emergency response',
  ],
  'public-services': [
    'Benefit eligibility assessment',
    'Service allocation',
    'Document processing',
    'Citizen inquiry handling',
    'Resource optimization',
  ],
  transport: [
    'Autonomous vehicles',
    'Route optimization',
    'Safety systems',
    'Traffic management',
    'Predictive maintenance',
  ],
  other: [
    'Customer service automation',
    'Content recommendation',
    'Sentiment analysis',
    'Process automation',
    'Predictive analytics',
  ],
};
